import { Suspense, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Session } from "@supabase/supabase-js";
import { CheckCircle2 } from "lucide-react";
import { AuthContext, useUser } from "@/hooks/useUser";
import { GuestEmailConfirmationForm } from "./GuestEmailConfirmationForm";
import { endGuestCheckout, guestCheckoutRequest, type GuestCheckoutInfo } from "@/lib/guest-checkout-api";
import { track } from "@/lib/analytics";
import { PageLoader } from "@/components/ui/spinner";
import { lazyRetry } from "@/lib/lazyRetry";
import { guestProfileCompletionFor } from "@/lib/guest-profile-completion";
import { fetchVerificationStatus } from "@/lib/users-api";

const InlineGuestProfile = lazyRetry(() => import("@/components/auth/AuthModal").then(module => ({
  default: module.GuestProfileCompletionForm,
})));

/** Only the proven mailbox owner can use the saved, unverified input as
 * editable defaults. The normal authenticated completion API remains in use. */
export function GuestProfileCompletionBoundary({ children }: { children: ReactNode }) {
  const auth = useUser();
  const { t } = useTranslation();
  const details = guestProfileCompletionFor(auth.user);
  if (!details || (!auth.needsProfile && auth.profile?.is_complete === true)) return <>{children}</>;
  if (auth.isLoading) return <PageLoader />;
  return <div className="mx-auto w-full max-w-xl px-4 py-8 sm:py-12">
    <p role="status" className="mb-4 text-sm font-medium text-primary">
      {t("checkout.guest.email_confirmed", { defaultValue: "Email confirmed." })}
    </p>
    <Suspense fallback={<PageLoader />}><InlineGuestProfile details={details} /></Suspense>
  </div>;
}

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

/** Runs only after actual, verified account sign-in/profile readiness. No
 * booking, payment, customer/card transfer, or personal-data update occurs. */
export function GuestBookingClaimCoordinator() {
  const auth = useUser();
  const queryClient = useQueryClient();
  const inFlight = useRef<string | null>(null);
  useEffect(() => {
    if (!auth.session || auth.isLoading ||
      auth.user?.app_metadata?.checkout_guest === true || !auth.user?.email_confirmed_at) return;
    const token = auth.session.access_token;
    const id = auth.user.id;
    if (inFlight.current === id) return;
    inFlight.current = id;
    const controller = new AbortController();
    void (async () => {
      const { guest } = await guestCheckoutRequest<{ guest: GuestCheckoutInfo | null }>("", { signal: controller.signal });
      if (controller.signal.aborted || !guest) return;
      if (guest.email.toLowerCase() !== auth.user?.email?.toLowerCase()) {
        // An unrelated real account must not inherit a previous visitor's
        // pending email/name or an original guest checkout cookie.
        await endGuestCheckout(guest.flowId);
        return;
      }
      if (!auth.profile?.is_complete) return;
      if (!guest.claimed) {
        const claim = await guestCheckoutRequest<{ claimedNow: boolean }>("/claim", { token, csrfToken: guest.csrfToken, body: {}, signal: controller.signal });
        if (claim.claimedNow) track("checkout_guest_email_confirmed", { checkout_journey_id: guest.flowId });
      }
      await queryClient.invalidateQueries({ queryKey: ["guest-bookings"] });
      window.dispatchEvent(new Event("checkout-guest-linked"));
    })().catch(() => {
      // Account access remains normal; explicit confirmation can retry.
      // Never log the contact, code or either account's credentials.
      if (!controller.signal.aborted) inFlight.current = null;
    });
    return () => { controller.abort(); inFlight.current = null; };
  }, [auth.session?.access_token, auth.profile?.is_complete, auth.isLoading, auth.user?.id, queryClient]);
  return null;
}

export function GuestEmailConfirmationNotice({ variant = "default" }: { variant?: "default" | "confirmation" } = {}) {
  const auth = useUser();
  const { t } = useTranslation();
  const profile = auth.profile as typeof auth.profile & { guest_email_confirmed?: boolean };
  const noticeDeadline = useRef(Date.now() + 60_000);
  const { data, isError, refetch } = useQuery({
    queryKey: ["guest-owned-bookings", auth.user?.id],
    queryFn: () => guestCheckoutRequest<{ bookings: { public_id: string }[] }>("/bookings", {
      token: auth.session!.access_token,
    }),
    enabled: auth.user?.app_metadata?.checkout_guest === true &&
      !!auth.session?.access_token && !profile?.guest_email_confirmed,
    retry: 1,
    staleTime: 30_000,
    refetchInterval: (query) => query.state.data?.bookings.length ||
      Date.now() >= noticeDeadline.current ? false : 5_000,
  });
  if (auth.user?.app_metadata?.checkout_guest !== true) return null;
  const confirmation = variant === "confirmation";
  if (profile?.guest_email_confirmed) return <section
    role="status"
    className={confirmation
      ? "flex max-w-md items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-5"
      : "flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-5 sm:p-6"}
    data-testid="panel-email-verified-success">
    <CheckCircle2 aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-green-700" />
    <div className="min-w-0">
      <h2 className="text-sm font-semibold text-green-900">
        {t("checkout.guest.email_confirmed", { defaultValue: "Email confirmed." })}
      </h2>
      <p className="mt-1 text-sm text-green-900">
        {t("checkout.guest.email_confirmed_booking", { defaultValue: "You can find this booking in My Trips." })}
      </p>
    </div>
  </section>;
  const panelClass = confirmation
    ? "max-w-md rounded-2xl border border-amber-200 bg-amber-50 p-5"
    : "rounded-2xl border border-border bg-background p-5 sm:p-6";
  if (isError) return <div role="alert" className={confirmation ? `${panelClass} text-sm text-amber-900` : "rounded-2xl border p-4"}>
    {t("calendar.loadError")}{" "}<button className="underline" type="button" onClick={() => void refetch()}>{t("common.try_again")}</button>
  </div>;
  if (!data?.bookings?.length) return null;
  return <section className={panelClass} aria-labelledby="guest-email-heading" data-testid="panel-email-verify-post-payment">
    <h2 id="guest-email-heading" className={confirmation ? "text-sm font-semibold text-amber-900" : "text-lg font-semibold"}>
      {t("checkout.guest.confirm_title", { defaultValue: "Confirm your email" })}
    </h2>
    <p className={`mt-2 text-sm ${confirmation ? "text-amber-900" : "text-muted-foreground"}`}>
      {t("checkout.guest.confirm_description", { defaultValue: "Add this booking to your account so you can find it whenever you sign in. Your booking and payment won't change." })}
    </p>
    <GuestEmailConfirmationForm key={`${auth.user?.id}:${auth.profile?.email}`} email={auth.profile?.email || ""} />
  </section>;
}

/** Other account routes remain real-account-only. A verified account can
 * explicitly open a linked guest booking with a booking-party session; its
 * account-wide profile/session outside this subtree never changes. */
export function GuestBookingBoundary({ publicId, children }: { publicId: string; children: ReactNode }) {
  const auth = useUser();
  const { t } = useTranslation();
  const base = useContext(AuthContext);
  const [scoped, setScoped] = useState<{ session: Session; profile: NonNullable<typeof auth.profile>; accountId: string; accountPhoneVerified: boolean | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedFor, setResolvedFor] = useState("");
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const scopeKey = `${auth.user?.id ?? ""}:${publicId}`;
  useEffect(() => {
    if (!auth.session || !auth.profile?.is_complete || auth.user?.app_metadata?.checkout_guest === true) return;
    const controller = new AbortController();
    const accountId = auth.user!.id;
    if (resolvedFor !== scopeKey) { setScoped(null); setLoading(true); }
    setError(false);
    void (async () => {
      try {
        const result = await guestCheckoutRequest<{ session: Session }>("/access", {
          token: auth.session!.access_token, body: { publicId }, signal: controller.signal,
        });
        const response = await fetch(`${API_BASE}/auth/ensure-user`, {
          method: "POST", signal: controller.signal,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${result.session.access_token}` },
          body: "{}",
        });
        if (!response.ok) throw new Error(t("checkout.guest.details_unavailable"));
        const data = await response.json();
        // Read with the proven account's token, never the isolated payer's.
        // Unknown status suppresses this optional prompt, not a payment gate.
        const accountVerification = await fetchVerificationStatus(auth.session!.access_token).catch(() => null);
        if (!controller.signal.aborted) setScoped({
          session: result.session, profile: data.profile, accountId,
          accountPhoneVerified: accountVerification?.phone_verified ?? null,
        });
      } catch (failure) {
        if (controller.signal.aborted) return;
        // A normal reservation is not a guest booking. Let its established
        // loader/ownership checks run without changing its identity.
        if ((failure as { status?: number }).status !== 404) setError(true);
      } finally {
        if (!controller.signal.aborted) { setResolvedFor(scopeKey); setLoading(false); }
      }
    })();
    return () => controller.abort();
  }, [publicId, auth.user?.id, auth.profile?.is_complete, retry, t]);
  useEffect(() => {
    if (!scoped?.session.expires_at) return;
    const timer = window.setTimeout(() => setRetry((value) => value + 1),
      Math.max(1000, scoped.session.expires_at * 1000 - Date.now() - 60_000));
    return () => window.clearTimeout(timer);
  }, [scoped?.session.expires_at]);
  if (auth.user?.app_metadata?.checkout_guest === true) return <>{children}</>;
  if (guestProfileCompletionFor(auth.user) && (auth.needsProfile || auth.profile?.is_complete !== true)) {
    return <GuestProfileCompletionBoundary>{children}</GuestProfileCompletionBoundary>;
  }
  if (!auth.user || !auth.profile?.is_complete) return <PageLoader />;
  if (loading || resolvedFor !== scopeKey) return <PageLoader />;
  if (error) return <div role="alert" className="p-6">
    <p>{t("calendar.loadError")}</p>
    <button type="button" className="mt-3 min-h-11 underline" onClick={() => setRetry((value) => value + 1)}>{t("common.try_again")}</button>
  </div>;
  if (!scoped || scoped.accountId !== auth.user?.id || !base) return <>{children}</>;
  return <AuthContext.Provider value={{
    ...base, session: scoped.session, user: scoped.session.user, profile: scoped.profile,
    linkedAccountPhoneVerified: scoped.accountPhoneVerified,
    isAdmin: false, adminRole: 0, permissions: [], isOwner: false, isLoading: false,
    profileError: false, needsProfile: false, hasPermission: () => false,
    setHostOverride: () => {},
    patchProfile: (patch) => setScoped((current) => current && current.accountId === auth.user?.id
      ? { ...current, profile: { ...current.profile, ...patch, id: current.profile.id, admin_role: 0, permissions: [] } }
      : current),
    retryProfile: () => {
      const expected = scoped.accountId;
      void fetch(`${API_BASE}/auth/ensure-user`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${scoped.session.access_token}` },
        body: "{}",
      }).then(async (response) => {
        if (!response.ok) throw new Error(t("checkout.guest.details_unavailable"));
        const data = await response.json();
        setScoped((current) => current?.accountId === expected ? { ...current, profile: data.profile } : current);
      }).catch(() => setError(true));
    },
  }}>{children}</AuthContext.Provider>;
}

export function GuestAccountProfile({ children }: { children: ReactNode }) {
  const auth = useUser();
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  if (auth.user?.app_metadata?.checkout_guest !== true) return <GuestProfileCompletionBoundary>{children}</GuestProfileCompletionBoundary>;
  return <main className="mx-auto w-full max-w-2xl space-y-6 px-4 py-8">
    <h1 className="text-3xl font-semibold">{t("checkout.identity.heading", { defaultValue: "Your details" })}</h1>
    <div className="rounded-2xl border border-border bg-background p-5">
      <p className="font-medium">{auth.profile?.first_name} {auth.profile?.last_name}</p>
      <p className="break-all text-muted-foreground">{auth.profile?.email}</p>
    </div>
    <GuestEmailConfirmationNotice />
    <button type="button" className="min-h-12 w-full rounded-xl border border-border p-3" onClick={() => navigate("/trips")}>
      {t("checkout.guest.my_bookings", { defaultValue: "My bookings" })}
    </button>
  </main>;
}
